/**
 * Clients - FBO customer/client management
 */

/** Client type classification */
export type ClientType = 'individual' | 'corporate' | 'government' | 'non_profit'

/** Client type display labels */
export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  individual: 'Individual',
  corporate: 'Corporate',
  government: 'Government',
  non_profit: 'Non-Profit',
}

/** All client types for select dropdowns */
export const CLIENT_TYPES: ClientType[] = [
  'individual',
  'corporate',
  'government',
  'non_profit',
]

/** Client status */
export type ClientStatus = 'lead' | 'prospect' | 'active' | 'inactive' | 'suspended'

/** Client status display labels */
export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  prospect: 'Prospect',
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
}

/** Badge variants for client status display */
export const CLIENT_STATUS_VARIANTS: Record<ClientStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  lead: 'outline',
  prospect: 'secondary',
  active: 'default',
  inactive: 'outline',
  suspended: 'destructive',
}

/** All client statuses */
export const CLIENT_STATUSES: ClientStatus[] = [
  'lead',
  'prospect',
  'active',
  'inactive',
  'suspended',
]

/** Payment terms options */
export const PAYMENT_TERMS_OPTIONS = [
  'due_on_receipt',
  'net_15',
  'net_30',
  'net_45',
  'net_60',
] as const

export type PaymentTerms = (typeof PAYMENT_TERMS_OPTIONS)[number]

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  due_on_receipt: 'Due on Receipt',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_45: 'Net 45',
  net_60: 'Net 60',
}

/** Database row type for clients table */
export interface ClientRow {
  id: string
  organization_id: string
  client_number: string
  client_type: ClientType
  first_name: string | null
  last_name: string | null
  company_name: string | null
  legal_entity_name: string | null
  email_primary: string | null
  phone_primary: string | null
  phone_secondary: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state_province: string | null
  postal_code: string | null
  country: string | null
  payment_terms: string | null
  tax_exempt: boolean
  tax_id: string | null
  status: ClientStatus
  preferences: Record<string, unknown> | null
  notes: string | null
  created_at: string
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
}

/** Insert type for creating new clients */
export interface ClientInsert {
  organization_id: string
  client_type: ClientType
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
  legal_entity_name?: string | null
  email_primary?: string | null
  phone_primary?: string | null
  phone_secondary?: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state_province?: string | null
  postal_code?: string | null
  country?: string | null
  payment_terms?: string | null
  tax_exempt?: boolean
  tax_id?: string | null
  status?: ClientStatus
  preferences?: Record<string, unknown> | null
  notes?: string | null
}

/** Update type for modifying clients */
export interface ClientUpdate {
  client_type?: ClientType
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
  legal_entity_name?: string | null
  email_primary?: string | null
  phone_primary?: string | null
  phone_secondary?: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state_province?: string | null
  postal_code?: string | null
  country?: string | null
  payment_terms?: string | null
  tax_exempt?: boolean
  tax_id?: string | null
  status?: ClientStatus
  preferences?: Record<string, unknown> | null
  notes?: string | null
}

/** Form data interface (what the form submits) */
export interface ClientFormData {
  client_type: ClientType
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
  legal_entity_name?: string | null
  email_primary?: string | null
  phone_primary?: string | null
  phone_secondary?: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state_province?: string | null
  postal_code?: string | null
  country?: string | null
  payment_terms?: string | null
  tax_exempt?: boolean
  tax_id?: string | null
  status: ClientStatus
  notes?: string | null
}

/** Table row type (subset for list display) */
export interface ClientTableRow {
  id: string
  client_number: string
  client_type: ClientType
  first_name: string | null
  last_name: string | null
  company_name: string | null
  email_primary: string | null
  phone_primary: string | null
  status: ClientStatus
  created_at: string
  deleted_at: string | null
}

/** Filter options for clients list */
export interface ClientFilters {
  search?: string
  type?: ClientType | null
  status?: ClientStatus | null
  recordStatus?: RecordStatusFilter
}

/** Helper function to get display name for a client */
export function getClientDisplayName(client: {
  client_type: ClientType
  company_name?: string | null
  first_name?: string | null
  last_name?: string | null
}): string {
  if (client.client_type === 'individual') {
    const parts = [client.first_name, client.last_name].filter(Boolean)
    return parts.length > 0 ? parts.join(' ') : 'Unnamed Client'
  }
  return client.company_name || 'Unnamed Company'
}

/** Check if client type requires company name */
export function isCompanyType(type: ClientType): boolean {
  return type !== 'individual'
}

// ============================================
// CLIENTS REGISTRY TYPES (v2)
// ============================================

import type { RecordStatusFilter } from '@/types/record-status'
import type { FilterPillOption } from '@/components/data-table'
import type { SavedViewTabConfig } from '@/components/data-table'

/**
 * Saved view identifiers for client registry
 */
export type ClientSavedViewId = 'portfolio' | 'leads' | 'at_risk' | 'all'

/**
 * Account type filter — reuses ClientType values
 */
export type AccountTypeFilter = ClientType

/**
 * Relationship filter — computed from lease/transient data
 */
export type ClientRelationshipFilter = 'based_tenant' | 'transient_guest' | 'both' | 'no_contract'

/**
 * CRM stage filter — mapped from client.status
 */
export type ClientCrmStageFilter = 'active' | 'lead' | 'prospect' | 'former' | 'inactive'

/**
 * Quick filter state for clients — independent multi-select dimensions
 */
export interface ClientQuickFilterState {
  accountType: AccountTypeFilter[]
  relationship: ClientRelationshipFilter[]
  crmStage: ClientCrmStageFilter[]
}

/**
 * Grouping options for client table
 */
export type ClientGroupingOption = 'relationship' | 'account_type' | 'crm_stage' | 'none'

/**
 * Anomaly flag types for inline badges
 */
export type ClientAnomalyFlag =
  | 'lease_expiring'       // Orange: lease expires within 90 days
  | 'inactive_client'      // Yellow: inactive status with past activity
  | 'repeat_visitor'       // Blue: 3+ transient visits, no lease (conversion opportunity)
  | 'overdue_invoices'     // Red: overdue invoices (Coming Soon)
  | 'payment_concern'      // Yellow: payment history concern (Coming Soon)
  | 'fuel_commitment_low'  // Yellow: fuel commitment below threshold (Coming Soon)
  | 'credit_hold'          // Red: account on credit hold (Coming Soon)

// ============================================
// SAVED VIEW CONFIGS
// ============================================

/**
 * Saved view configuration for clients
 */
export interface ClientSavedViewConfig extends SavedViewTabConfig<ClientSavedViewId> {
  defaultFilters: ClientQuickFilterState
  defaultGroupBy: ClientGroupingOption
}

export const CLIENT_SAVED_VIEW_CONFIGS: ClientSavedViewConfig[] = [
  {
    id: 'portfolio',
    label: 'Portfolio',
    description: 'Active clients with current agreements',
    defaultFilters: {
      accountType: [],
      relationship: [],
      crmStage: ['active'],
    },
    defaultGroupBy: 'none',
  },
  {
    id: 'all',
    label: 'All',
    description: 'Every client in the CRM',
    defaultFilters: {
      accountType: [],
      relationship: [],
      crmStage: [],
    },
    defaultGroupBy: 'none',
  },
  {
    id: 'leads',
    label: 'Leads',
    description: 'Leads and prospects in the pipeline',
    defaultFilters: {
      accountType: [],
      relationship: [],
      crmStage: ['lead', 'prospect'],
    },
    defaultGroupBy: 'crm_stage',
  },
  {
    id: 'at_risk',
    label: 'At Risk',
    description: 'Clients with risk flags requiring attention',
    defaultFilters: {
      accountType: [],
      relationship: [],
      crmStage: ['active'],
    },
    defaultGroupBy: 'none',
  },
]

export function getClientSavedViewConfig(viewId: ClientSavedViewId): ClientSavedViewConfig {
  return CLIENT_SAVED_VIEW_CONFIGS.find((v) => v.id === viewId) ?? CLIENT_SAVED_VIEW_CONFIGS[1]
}

// ============================================
// FILTER PILL OPTIONS
// ============================================

export const ACCOUNT_TYPE_OPTIONS: FilterPillOption[] = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'individual', label: 'Individual' },
  { value: 'government', label: 'Government' },
  { value: 'non_profit', label: 'Non-Profit' },
]

export const CLIENT_RELATIONSHIP_OPTIONS: FilterPillOption[] = [
  { value: 'based_tenant', label: 'Based Tenant', icon: '🏠' },
  { value: 'transient_guest', label: 'Transient Guest', icon: '✈️' },
  { value: 'both', label: 'Both', icon: '🔄' },
  { value: 'no_contract', label: 'No Contract', icon: '⚠️' },
]

export const CLIENT_RELATIONSHIP_LABELS: Record<ClientRelationshipFilter, string> = {
  based_tenant: 'Based Tenant',
  transient_guest: 'Transient Guest',
  both: 'Both',
  no_contract: 'No Contract',
}

export const CLIENT_RELATIONSHIP_VARIANTS: Record<ClientRelationshipFilter, 'default' | 'secondary' | 'outline'> = {
  based_tenant: 'default',
  transient_guest: 'secondary',
  both: 'default',
  no_contract: 'outline',
}

export const CLIENT_CRM_STAGE_OPTIONS: FilterPillOption[] = [
  { value: 'active', label: 'Active', icon: '🟢' },
  { value: 'lead', label: 'Lead', icon: '🟡' },
  { value: 'prospect', label: 'Prospect', icon: '🔵' },
  { value: 'former', label: 'Former', icon: '⚪' },
  { value: 'inactive', label: 'Inactive', icon: '🔴' },
]

export const CLIENT_CRM_STAGE_LABELS: Record<ClientCrmStageFilter, string> = {
  active: 'Active',
  lead: 'Lead',
  prospect: 'Prospect',
  former: 'Former',
  inactive: 'Inactive',
}

export const CLIENT_CRM_STAGE_VARIANTS: Record<ClientCrmStageFilter, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  lead: 'outline',
  prospect: 'secondary',
  former: 'outline',
  inactive: 'destructive',
}

// ============================================
// COLUMN VISIBILITY — Always-on + View-contextual
// ============================================

/**
 * 7 always-on columns visible in every saved view
 */
export const CLIENT_ALWAYS_ON_COLUMNS = [
  'client_name',
  'client_type',
  'primary_contact',
  'aircraft_count',
  'crm_stage',
  'relationship',
  'active_contracts',
] as const

/**
 * Contextual columns that appear only for specific saved views
 */
export const CLIENT_VIEW_CONTEXTUAL_COLUMNS: Record<ClientSavedViewId, string[]> = {
  portfolio: ['current_location', 'member_since'],
  all: ['current_location', 'member_since'],
  leads: ['lead_source', 'last_contact', 'lead_score'],
  at_risk: ['risk_flag', 'lease_expiry_days', 'nearest_lease_end'],
}

// ============================================
// GROUPING OPTIONS
// ============================================

export interface ClientGroupingOptionConfig {
  value: ClientGroupingOption
  label: string
  description: string
}

export const CLIENT_GROUPING_OPTIONS: ClientGroupingOptionConfig[] = [
  { value: 'relationship', label: 'By Relationship', description: 'Based Tenant / Transient Guest / Both / No Contract' },
  { value: 'account_type', label: 'By Account Type', description: 'Individual / Corporate / Government / Non-Profit' },
  { value: 'crm_stage', label: 'By CRM Stage', description: 'Active / Lead / Prospect / Former / Inactive' },
  { value: 'none', label: 'None', description: 'Flat list' },
]

// ============================================
// ANOMALY FLAGS
// ============================================

export interface ClientAnomalyFlagConfig {
  flag: ClientAnomalyFlag
  label: string
  emoji: string
  color: 'red' | 'orange' | 'yellow' | 'blue'
  bgClass: string
  textClass: string
  comingSoon?: boolean
}

export const CLIENT_ANOMALY_FLAGS: ClientAnomalyFlagConfig[] = [
  { flag: 'overdue_invoices', label: 'Overdue invoices', emoji: '🔴', color: 'red', bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-700 dark:text-red-300', comingSoon: true },
  { flag: 'credit_hold', label: 'Credit hold', emoji: '🔴', color: 'red', bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-700 dark:text-red-300', comingSoon: true },
  { flag: 'lease_expiring', label: 'Lease expiring', emoji: '🟠', color: 'orange', bgClass: 'bg-orange-100 dark:bg-orange-900/30', textClass: 'text-orange-700 dark:text-orange-300' },
  { flag: 'inactive_client', label: 'Inactive', emoji: '🟡', color: 'yellow', bgClass: 'bg-yellow-100 dark:bg-yellow-900/30', textClass: 'text-yellow-700 dark:text-yellow-300' },
  { flag: 'payment_concern', label: 'Payment concern', emoji: '🟡', color: 'yellow', bgClass: 'bg-yellow-100 dark:bg-yellow-900/30', textClass: 'text-yellow-700 dark:text-yellow-300', comingSoon: true },
  { flag: 'fuel_commitment_low', label: 'Low fuel commitment', emoji: '🟡', color: 'yellow', bgClass: 'bg-yellow-100 dark:bg-yellow-900/30', textClass: 'text-yellow-700 dark:text-yellow-300', comingSoon: true },
  { flag: 'repeat_visitor', label: 'Repeat visitor', emoji: '🔵', color: 'blue', bgClass: 'bg-blue-100 dark:bg-blue-900/30', textClass: 'text-blue-700 dark:text-blue-300' },
]

// ============================================
// EXTENDED ROW TYPE
// ============================================

/**
 * Extended client row with joined/computed data for the registry view
 */
export interface ClientRegistryRow extends ClientRow {
  // Joined from contacts
  primary_contact_name: string | null
  primary_contact_email: string | null
  primary_contact_phone: string | null
  contact_count: number

  // Computed from aircraft table
  aircraft_count: number
  aircraft_tail_numbers: string[]

  // Computed from lease_contracts
  active_lease_count: number
  active_lease_monthly_total: number | null
  nearest_lease_end_date: string | null
  lease_location_names: string[]

  // Computed from transient_stays
  active_transient_count: number
  transient_visits_12mo: number

  // Computed from lease + transient locations combined
  allocation_location_count: number
  allocation_location_names: string[]

  // Computed relationship
  computed_relationship: ClientRelationshipFilter

  // CRM stage (mapped from client.status)
  crm_stage: ClientCrmStageFilter

  // Anomaly flags (computed client-side)
  anomalies: ClientAnomalyFlag[]
}

/**
 * Summary data for the footer bar
 */
export interface ClientRegistrySummary {
  totalClients: number
  activeCount: number
  leadCount: number
  prospectCount: number
  formerCount: number
  inactiveCount: number
  basedTenantCount: number
  transientGuestCount: number
  bothCount: number
  noContractCount: number
  atRiskCount: number
  leasesExpiringCount: number
  monthlyCommittedTotal: number
  arOutstanding: number
}

/**
 * Compute anomaly flags for a client registry row
 */
export function computeClientAnomalyFlags(row: ClientRegistryRow): ClientAnomalyFlag[] {
  const flags: ClientAnomalyFlag[] = []
  const now = new Date()

  // Lease expiring within 90 days
  if (row.nearest_lease_end_date) {
    const leaseEnd = new Date(row.nearest_lease_end_date)
    const daysUntilExpiry = Math.ceil((leaseEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (daysUntilExpiry > 0 && daysUntilExpiry <= 90) {
      flags.push('lease_expiring')
    }
  }

  // Inactive client
  if (row.status === 'inactive' || row.status === 'suspended') {
    flags.push('inactive_client')
  }

  // Repeat visitor: 3+ transient visits in 12 months, no active lease
  if (row.transient_visits_12mo >= 3 && row.active_lease_count === 0) {
    flags.push('repeat_visitor')
  }

  return flags
}

/**
 * Map client status to CRM stage
 */
export function mapClientStatusToCrmStage(status: ClientStatus): ClientCrmStageFilter {
  switch (status) {
    case 'active': return 'active'
    case 'lead': return 'lead'
    case 'prospect': return 'prospect'
    case 'inactive': return 'inactive'
    case 'suspended': return 'inactive'
    default: return 'inactive'
  }
}
